import {
  cloneDeep,
  get,
  isEmpty,
  isEqual,
  isNaN,
  isNull,
  isNumber,
  isString,
  isArray,
  isUndefined,
  uniqWith,
} from 'lodash'
import { FIELD_PROPS } from './consts'
import { FIELD_FUNCTION_TWO_GROUP_RE, FIELDS_TWO_GROUP_RE } from './form'
import { CORE_RE } from './store/modules/raw-sql/constants'
import { getFieldFunctionAstMatches } from '@hydra/common'

export const PATH_SEPARATOR = '/'
export const LAT_LON_SEPARATOR = ';'
export const ERROR_MSG_TYPE = {
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  SUCCESS: 'success',
}

export const IMAGE_TYPES = ['image/gif', 'image/jpeg', 'image/png']

export const LIST_EXPORT_TYPE_PROPERTIES = {
  html: { key: 'HTML', value: 'HTML' },
  csv: { key: 'CSV', value: 'CSV' },
  txt: { key: 'TEXT', value: 'TEXT' },
  pdf: { key: 'PDF', value: 'PDF' },
  xlsx: { key: 'EXCEL2007', value: 'Excel 2007 +' },
  ods: { key: 'ODS', value: 'OpenDocument Spreadsheet' },
}

export const EMPTY_DROPDOWN_LIST = {
  options: [],
  completed: true,
}

export const VARIABLE_TYPES = {
  STRING: 'string',
  OBJECT: 'object',
  NUMBER: 'number',
  BOOLEAN: 'boolean',
  FUNCTION: 'function',
  UNDEFINED: 'undefined',
}

export function isTruthy(value) {
  const type = typeof value
  let result
  switch (type) {
    case VARIABLE_TYPES.STRING:
      result = !['false', '0', ''].includes(value.toLowerCase())
      break
    case VARIABLE_TYPES.OBJECT:
      result = !isEmpty(value)
    case VARIABLE_TYPES.NUMBER:
    case VARIABLE_TYPES.BOOLEAN:
    default:
      result = !!value
  }
  return result
}

export function exportTypes(translate, types = []) {
  return {
    options: types.map((type) => ({
      value: translate(LIST_EXPORT_TYPE_PROPERTIES[type].value),
      key: LIST_EXPORT_TYPE_PROPERTIES[type].key,
    })),
    completed: true,
  }
}

export function exportLength(self) {
  return {
    options: [
      { key: 'ACT', value: `${self.$i18n.t('export_this_page')}` },
      { key: 'ALL', value: `${self.$i18n.t('export_all_pages')}` },
    ],
    completed: true,
  }
}

/**
 *
 * @param header
 * @return {*[]}
 */
export function parseJsonHead(header) {
  let messages = []
  if (header.SUCCESS.length) {
    header.SUCCESS.forEach((headerSuccess) => {
      messages.push({ name: headerSuccess.ID || '', msg: headerSuccess.PUBLIC, type: ERROR_MSG_TYPE.SUCCESS })
    })
  }

  if (header.ERROR.length) {
    header.ERROR.forEach((headerError) => {
      if (Array.isArray(headerError.ID) && headerError.ID.length) {
        for (const i in headerError.ID) {
          messages.push({
            name: headerError.ID[i] || '',
            page: headerError.PAGEID && headerError.PAGEID[i] ? headerError.PAGEID[i] : null,
            pageLabel: headerError.PAGELABEL && headerError.PAGELABEL[i] ? headerError.PAGELABEL[i] : null,
            label: headerError.LABEL && headerError.LABEL[i] ? headerError.LABEL[i] : '',
            msg: headerError.PUBLIC,
            type: ERROR_MSG_TYPE.ERROR,
            interpolate: headerError.PUBLIC_PARAMS,
          })
        }
      } else {
        const id = !Array.isArray(headerError.ID) && headerError.ID ? headerError.ID : ''
        messages.push({
          name: !isNull(headerError.ROWNUM) ? `${id}[${headerError.ROWNUM}]` : id,
          page: headerError.PAGEID,
          pageLabel: headerError.PAGELABEL,
          label: headerError.LABEL,
          msg: headerError.PUBLIC,
          type: ERROR_MSG_TYPE.ERROR,
          interpolate: headerError.PUBLIC_PARAMS,
        })
      }
    })
  }

  if (header.WARNING.length) {
    header.WARNING.forEach((headerWarning) => {
      if (Array.isArray(headerWarning.ID) && headerWarning.ID.length) {
        headerWarning.ID.forEach((hwId) => {
          messages.push({
            name: hwId || '',
            label: headerWarning.LABEL,
            msg: headerWarning.PUBLIC,
            type: ERROR_MSG_TYPE.WARNING,
            interpolate: headerWarning.PUBLIC_PARAMS,
          })
        })
      } else {
        const id = !Array.isArray(headerWarning.ID) && headerWarning.ID ? headerWarning.ID : ''
        messages.push({
          name: id,
          label: headerWarning.LABEL,
          msg: headerWarning.PUBLIC,
          type: ERROR_MSG_TYPE.WARNING,
          interpolate: headerWarning.PUBLIC_PARAMS,
        })
      }
    })
  }

  return messages
}

export function omit(obj = {}, keys) {
  return Object.entries(obj).reduce((field, [key, value]) => {
    if (!keys.includes(key)) {
      field[key] = value
    }
    return field
  }, {})
}

export function pick(obj = {}, keys) {
  return Object.entries(obj).reduce((field, [key, value]) => {
    if (keys.includes(key)) {
      field[key] = value
    }
    return field
  }, {})
}

export function dedupe(source) {
  return source.filter((el, i, arr) => arr.indexOf(el) === i)
}

export function randomString(length = 8, chars) {
  chars = chars || '_-ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789¡¢£¥¦§©ª«¬®¯°±µ·»¿'
  return Array(length)
    .fill(null)
    .map((char) => {
      return chars.charAt(Math.floor(Math.random() * chars.length))
    })
    .join('')
}

export function getLanguage() {
  const core = window.CORE
  if ('Intl' in window && core && core.language && core.language.code) {
    let isSupported = false
    try {
      isSupported = Intl.DateTimeFormat.supportedLocalesOf([core.language.code]).length > 0
    } catch (err) {
      // @todo log
      // console.log(err.message)
      null
    }
    if (isSupported) {
      return core.language.code
    }
  }
  if (navigator.languages) {
    return navigator.languages[0]
  }
  return navigator.language
}

export function getLanguageCode() {
  return getLanguage().split('-')[0]
}

export function resolveUrlParams(btn, route) {
  let tmpUrl = btn.url
  if (tmpUrl) {
    const matches = tmpUrl.match(/:\w+/g) // @regex matches anything in the url with the :EXAMPLE format
    if (matches) {
      matches.forEach((m) => {
        let urlParam = m.replace(':', '')
        let coreParam = get(window.CORE, ['params', urlParam], false)
        tmpUrl = tmpUrl.replace(m, coreParam, '')
      })
    }
  }
  return tmpUrl
}

export function createStorage(key) {
  const hasContent = () => {
    return !!localStorage.getItem(key)
  }

  const setStoreContent = (data) => {
    localStorage.setItem(key, JSON.stringify(data))
  }

  const getStoreContent = () => {
    const item = localStorage.getItem(key)
    return JSON.parse(item)
  }

  const setItemValue = (keyProp, data) => {
    const storedItems = getStoreContent()
    if (storedItems) {
      storedItems[keyProp] = data
      setStoreContent(storedItems)
    }
  }

  const getItemValue = (keyProp) => {
    const storedItem = getStoreContent()
    if (storedItem) {
      return storedItem[keyProp]
    }
  }

  const removeItemValue = (keyProp) => {
    const storedItem = getStoreContent()
    if (storedItem && storedItem[keyProp]) {
      delete storedItem[keyProp]
      setStoreContent(storedItem)
    }
  }

  const resetStorage = () => {
    localStorage.setItem(key, JSON.stringify({}))
  }

  const removeStorage = () => {
    localStorage.removeItem(key)
  }

  const isEmpty = () => getStoreContent().then((item) => item === null)

  return {
    hasContent,
    setStoreContent,
    getStoreContent,
    isEmpty,
    removeStorage,
    removeItemValue,
    resetStorage,
    setItemValue,
    getItemValue,
  }
}

/**
 * @description el-select option-jeit allitja elo listabol (objectlistbol, vagy entriesbol)
 * @param i18n
 * @param options
 * @param localizeData
 * @returns {{options: *[], completed: *}|unknown[]}
 */
export function convertOptions(i18n, options = [], localizeData = false) {
  const optionsClone = cloneDeep(options)
  let validOptions = []
  if (!isEmpty(optionsClone)) {
    if (isObject(optionsClone)) {
      if (!this.$CORE.isEnvProduction) {
        console.error(`Ide mar nem kene befutnunk! Milyen eset ez? ${JSON.stringify(optionsClone)}`)
      }
      const collator = new Intl.Collator(getLanguage())
      return Object.entries(optionsClone)
        .map((entry) => {
          if (Array.isArray(entry) && entry.length === 2) {
            return {
              // @TODO nem stringcastolunk backenden, ha lesz ezzel baj, akkor itt kell megfeleloen alakitani, ha nem, akkor ez delete
              key: entry[0], // keyToNumber(entry[0], isBooleanList, hasKeyAsString),
              value: String(entry[1]),
            }
          }
        })
        .sort(([, a], [, b]) => collator.compare(a, b))
    }
    if (Array.isArray(optionsClone)) {
      optionsClone.map((entry) => {
        if (isEntryValid(entry)) {
          entry.value = isNull(entry.value) ? (localizeData ? 'not_set' : i18n.t('not_set')) : String(entry.value)
          // @TODO nem stringcastolunk backenden, ha lesz ezzel baj, akkor itt kell megfeleloen alakitani, ha nem, akkor ez delete
          // entry.key = keyToNumber(entry.key, isBooleanList, hasKeyAsString)
          entry.key = isNull(entry.key) ? 'null' : entry.key
          if (localizeData) entry.value = i18n.t(entry.value)
          validOptions.push(entry)
        }
      })
    }
  }
  return validOptions
}

/**
 * @description el-select option elemeinek hasznalhatosagat ellenorzi
 * @param entry
 * @returns {boolean}
 */
export function isEntryValid(entry) {
  return (
    isObject(entry) &&
    entry.hasOwnProperty('key') &&
    entry.hasOwnProperty('value') &&
    //@TODO || isNull(entry.key) - ez nincs lekezelve backenden
    (isNumber(entry.key) || isString(entry.key)) &&
    !isUndefined(entry.value) &&
    !isEmpty(String(entry.value))
  )
}

/**
 * @description Booleanlista eseten (jellemzoen Igen/Nem checkbox szuroje) stringkent kell az options kulcsait hagyni
 * Egyebkent numberre kell alakitani
 * @param key
 * @returns {*|number}
 */
export function keyToNumber(key, isBooleanList = false, hasKeyAsString = false) {
  return isNaN(+key) || isBooleanList || hasKeyAsString ? key : parseInt(key)
}

export function convertBlobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = reject
    reader.onload = () => {
      resolve(reader.result)
    }
    reader.readAsDataURL(blob)
  })
}

export function concatPath(arr) {
  return arr.join(PATH_SEPARATOR)
}

export function parsePath(path) {
  if (!path) return []
  return path.split(PATH_SEPARATOR)
}

async function getUserDefinedFunctions(client) {
  const userDefinedResponse = await client.execute({
    operationId: 'UserFunction_getDefinedFunctions',
  })
  const userDefinedFunctions = get(userDefinedResponse, 'obj', '')
  sessionStorage.setItem('userDefinedFunctions', userDefinedFunctions)

  return userDefinedFunctions
}

export async function setUserDefinedFunctions(client) {
  let userDefinedFunctions = sessionStorage.getItem('userDefinedFunctions')

  if (!userDefinedFunctions) {
    userDefinedFunctions = await getUserDefinedFunctions(client)
  }

  const fn = new Function(`${userDefinedFunctions}; return UserDefined;`)
  window.UserDefined = fn()
}

if (!String.prototype.format) {
  String.prototype.format = function () {
    let args = arguments
    return this.replace(/{(\d+)}/g, function (match, number) {
      return typeof args[number] != 'undefined' ? args[number] : match
    })
  }
}

export function exceptionFormat(except, type) {
  return !except.includes(type)
}

export function convertToBool(value) {
  if (typeof value === 'string') {
    if (value === 'true') {
      return true
    } else if (value === undefined) {
      return false
    } else {
      if (isNaN(Number(value))) {
        if (value === 'false') {
          return false
        } else {
          return true
        }
      } else {
        value = Number(value)
      }
    }
  }
  return !!value
}

/**
 * @description model zarolasara vonatkozo esemeny kuldese socketen
 * @param type
 * @param model
 * @param id
 */
export function emitModelLock(type, model, id) {
  window.CORE.socket.emit(type, {
    id,
    type: model,
    userId: window.CORE.externalParams.user,
    system: window.CORE.subSystem.id,
  })
}

/**
 * @description Visszaadja, hogy a kapott érték Object-e
 * @param value
 * @returns {boolean}
 */
export function isObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * @description Turns a floating point number into string representation of floating number rounded to defined precision
 * @param float
 * @param precision
 * @param thousandSeparated
 * @returns {string}
 */
export function floatToPrecisionString(float, precision = 0, thousandSeparated = true) {
  let decimalSeparator = ','
  let thousandSeparator = thousandSeparated ? ' ' : ''
  const langCode = get(window, 'CORE.language.code')
  switch (langCode) {
    case 'en-US':
      decimalSeparator = '.'
      thousandSeparator = thousandSeparated ? ',' : ''
      break
    default:
      break
  }

  if (precision < 0) return float.toString()
  let floatString = float.toString()
  const fraction = floatString.split('.')[1]
  if (fraction) {
    for (let i = fraction.length; i < precision; i++) {
      floatString += '0'
    }
  } else {
    if (precision > 0) {
      floatString += '.'
      for (let i = 0; i < precision; i++) {
        floatString += '0'
      }
    }
  }
  let reverseString = floatString.split('').reverse()
  let final = ''
  let counter = 0
  let decimalIndex = -1
  for (let j = 0; j < reverseString.length; j++) {
    if (reverseString[j] === '.') {
      decimalIndex = j
    }
  }
  if (decimalIndex != -1) {
    for (let h = 0; h < decimalIndex; h++) {
      final += reverseString[h]
    }
    final += ','
  }
  for (let g = decimalIndex + 1; g < reverseString.length; g++) {
    if (counter > 0 && counter % 3 === 0) {
      final += ' '
    }
    final += reverseString[g]
    counter++
  }
  floatString = final.split('').reverse().join('')
  floatString = floatString.replace(',', decimalSeparator).replaceAll(' ', thousandSeparator)

  return floatString
}

/**
 * @description Surrounds text with highlight classed span element
 * @param sourceText
 * @param searchText
 * @param className
 * @returns {{success: boolean, html: *}}
 */
export function highlight(sourceText, searchText, className = 'highlight') {
  sourceText = String(sourceText)
  const exp = new RegExp(`(${searchText.replace(/([[()#$^*])/g, '\\$1')})`, 'ig') // @regex case-insensitive search regex for searchText
  const position = sourceText.search(exp)
  const replacedString = sourceText.replace(exp, `<span class='${className}'>$1</span>`)
  return {
    success: position >= 0,
    html: replacedString,
  }
}

export function dropdownHasValue(value) {
  if (isObject(value) || isNull(value) || isUndefined(value)) return false
  return !!String(value).trim()
}

export function isSubFormPath(path) {
  return path.split('/').length === 6
}

export function getDateFormatByLocale(localeCode) {
  let format = ''
  switch (localeCode) {
    case 'en-US': {
      format = 'MM/DD/yyyy'
      break
    }
    case 'hu-HU':
    case 'de-DE': {
      format = 'yyyy-MM-DD'
      break
    }
    default: {
      format = 'DD/MM/yyyy'
      break
    }
  }
  return format
}

export function getDateTimeFormatByLocale(localeCode) {
  let format = ''
  switch (localeCode) {
    case 'en-US': {
      format = 'MM/DD/yyyy hh:mm A'
      break
    }
    case 'hu-HU':
    case 'de-DE': {
      format = 'yyyy-MM-DD HH:mm'
      break
    }
    default: {
      format = 'DD/MM/yyyy hh:mm A'
      break
    }
  }
  return format
}

export function getTimeFormatByLocale(localeCode) {
  let format = ''
  switch (localeCode) {
    case 'en-US': {
      format = 'hh:mm:ss A'
      break
    }
    case 'hu-HU':
    case 'de-DE': {
      format = 'HH:mm:ss'
      break
    }
    default: {
      format = 'hh:mm:ss A'
      break
    }
  }
  return format
}

export function formatDateByTypeAndLocale(date, locale, type) {
  switch (type) {
    case 'date': {
      return this.$moment(date).format(getDateFormatByLocale(locale))
    }
    case 'time': {
      const [hours, minutes, seconds] = date.split(':')
      return new Date(null, null, null, hours, minutes, seconds).toLocaleTimeString(locale)
    }
    default: {
      return this.$moment(date).format(`${getDateFormatByLocale(locale)} HH:mm:ss`)
    }
  }
}

/**
 *
 * @param string
 * @param core
 * @returns {*}
 */
export function resolveCoreParam({ string = '', core }) {
  const coreMatch = string.match(CORE_RE)
  if (!coreMatch) return string
  return get(core, coreMatch[1])
}

/**
 * Resolve CORE params recursive
 * @param {*} input 
 * @param {*} core 
 * @returns 
 */
export function recursiveResolveCoreParam({ input = '', core }) {
  if (!isObject(input) || isNull(input) || isUndefined(input)) {
    if (isString(input)) {
      const coreMatch = input.match(CORE_RE)

      if (!coreMatch) return input

      return get(core, coreMatch[1])
    }

    return input
  }

  if (isArray(input)) {
    return input.map(item => recursiveResolveCoreParam({ input: item, core }))
  }

  const result = {}
  for (const [key, value] of Object.entries(input)) {
    result[key] = recursiveResolveCoreParam({ input: value, core })
  }

  return result
}

export function hasValue(value) {
  return (
    !isNull(value) &&
    !isUndefined(value) &&
    ((isObject(value) && !isEmpty(value)) ||
      (!isObject(value) && value.toString().length) ||
      (Array.isArray(value) && value.length))
  )
}

/**
 * @description Visszaadja, hogy kapott expression milyen mezo, melyik propjatol fugg
 * @param expression
 * @returns {*[]}
 */
export function parseFieldDependencies(expression) {
  if (typeof expression !== 'string') return []
  const fieldFunctionMatches = Array.from(expression.matchAll(FIELD_FUNCTION_TWO_GROUP_RE))
  const fieldFunctionAstMatches = getFieldFunctionAstMatches(expression)
  const fieldObjectMatches = Array.from(expression.matchAll(FIELDS_TWO_GROUP_RE))
  const fieldDependencies = [ ...fieldFunctionMatches, ...fieldFunctionAstMatches, ...fieldObjectMatches ]
  let dependencies = []
  if (fieldDependencies.length) {
    for (const dependency of fieldDependencies) {
      const [ _, fieldName, prop = 'value' ] = dependency
      dependencies.push({ fieldName, prop })
    }
  }
  return uniqWith(dependencies, isEqual)
}

export function getFormattedValue(list, value, taggable) {
  if (taggable) {
    value = value.map((item) => {
      return isObject(item) ? item.key : item
    })
  }
  let formattedValue = ''
  if (Array.isArray(list)) {
    formattedValue = list.reduce((acc, item) => {
      if (!item) {
        console.error('Invalid list item / listExp result', item)
        return acc
      }
      if (Array.isArray(value)) {
        if (value.indexOf(item.key) > -1) {
          acc += acc.length ? `, ${item.value}` : `${item.value}`
        }
      } else if (value == item.key) {
        acc = `${item.value}`
      }
      return acc
    }, '')
  } else {
    formattedValue = get(list, value, '')
  }
  return formattedValue
}

export function needToSkip({ isCreate, change, prop, fieldsToSkip }) {
  return (
    (!isCreate && change.valueIsDefault && change.propToChange === FIELD_PROPS.VALUE) ||
    (fieldsToSkip.includes(change.changes) && change.propToChange === FIELD_PROPS.VALUE)
  )
}

export function objectToKeyValueList(keyValues) {
  return {
    options: Object.entries(keyValues).map(([key, value]) => ({ key, value })),
    completed: true,
  }
}

// @TODO:dropdown egyszer majd keyValue to labelValue mindenhol!!
export function objectToLabelValueList(keyValues) {
  return {
    options: Object.entries(keyValues).map(([key, value]) => ({ value: key, label: value })),
    completed: true,
  }
}

export const codeMirrorThemes = [
  "default",
  "3024-day",
  "3024-night",
  "abbott",
  "abcdef",
  "ambiance",
  "ayu-dark",
  "ayu-mirage",
  "base16-dark",
  "base16-light",
  "bespin",
  "blackboard",
  "cobalt",
  "colorforth",
  "darcula",
  "dracula",
  "duotone-dark",
  "duotone-light",
  "eclipse",
  "elegant",
  "erlang-dark",
  "gruvbox-dark",
  "hopscotch",
  "icecoder",
  "idea",
  "isotope",
  "juejin",
  "lesser-dark",
  "liquibyte",
  "lucario",
  "material",
  "material-darker",
  "material-palenight",
  "material-ocean",
  "mbo",
  "mdn-like",
  "midnight",
  "monokai",
  "moxer",
  "neat",
  "neo",
  "night",
  "nord",
  "oceanic-next",
  "panda-syntax",
  "paraiso-dark",
  "paraiso-light",
  "pastel-on-dark",
  "railscasts",
  "rubyblue",
  "seti",
  "shadowfox",
  "solarized dark",
  "solarized light",
  "the-matrix",
  "tomorrow-night-bright",
  "tomorrow-night-eighties",
  "ttcn",
  "twilight",
  "vibrant-ink",
  "xq-dark",
  "xq-light",
  "yeti",
  "yonce",
  "zenburn"
]
export function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const isMac = () => navigator.userAgent.includes('Mac')

export function clearCookie(name) {
  const pathBits = location.pathname.split('/')
  let pathCurrent = ' path='
  // do a simple pathless delete first.
  document.cookie = `${name}=; expires=Thu, 01-Jan-1970 00:00:01 GMT;${pathCurrent};`
  for (const element of pathBits) {
    pathCurrent += ((pathCurrent.substr(-1) !== '/') ? '/' : '') + element
    document.cookie = `${name}=; expires=Thu, 01-Jan-1970 00:00:01 GMT;${pathCurrent};`
  }
}
